import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea } from "@/components/tools/fields";

const AREAS = ["EHCP Reviews", "Meetings", "Parent Contact", "Administration", "Data Analysis", "Staff Support", "Training", "Teaching"];
const key = (a) => a.toLowerCase().replace(/\s+/g, "-");

export const WorkloadAudit = () => {
  const [data, setData, reset] = useStoredState("workload-audit-v4", { hours: {}, most: "", streamline: "", support: "" });
  const hrs = (a) => Math.max(0, parseFloat(data.hours?.[key(a)]) || 0);
  const total = AREAS.reduce((s, a) => s + hrs(a), 0);
  const top = total ? AREAS.reduce((m, a) => (hrs(a) > hrs(m) ? a : m), AREAS[0]) : null;
  const setHours = (a, v) => setData((d) => ({ ...d, hours: { ...d.hours, [key(a)]: v } }));
  return (
    <ToolShell id="workload-audit" onReset={reset}>
      <div className="overflow-hidden rounded-2xl border-2 border-line">
        <table className="w-full text-left">
          <caption className="sr-only">Hours per week spent on each area</caption>
          <thead className="bg-surface">
            <tr>
              <th scope="col" className="px-4 py-3 text-sm font-bold">Activity</th>
              <th scope="col" className="w-36 px-4 py-3 text-sm font-bold sm:w-44">Hours</th>
              <th scope="col" className="hidden px-4 py-3 text-sm font-bold sm:table-cell">Share of time</th>
            </tr>
          </thead>
          <tbody>
            {AREAS.map((a) => {
              const pct = total ? Math.round((hrs(a) / total) * 100) : 0;
              return (
                <tr key={a} className="border-t border-line">
                  <th scope="row" className="px-4 py-3 font-bold">
                    <label htmlFor={`wa-${key(a)}`}>{a}</label>
                    {top === a && <span className="ml-2 rounded-full bg-sun px-2 py-0.5 text-xs text-sun-fg">Most time</span>}
                  </th>
                  <td className="px-4 py-2">
                    <input id={`wa-${key(a)}`} type="number" inputMode="decimal" min="0" step="0.5" value={data.hours?.[key(a)] ?? ""}
                      onChange={(e) => setHours(a, e.target.value)} className="field-input py-2" data-testid={`wa-hours-${key(a)}`} />
                  </td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <div className="flex items-center gap-3">
                      <div className="h-3 flex-1 overflow-hidden rounded-full bg-line" aria-hidden="true">
                        <div className="h-full rounded-full bg-brand transition-[width] duration-700" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right text-sm font-bold">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink bg-surface">
              <th scope="row" className="px-4 py-3 font-bold">Total weekly hours</th>
              <td className="px-4 py-3 font-display text-2xl" data-testid="wa-total" aria-live="polite">{total} hrs</td>
              <td className="hidden px-4 py-3 text-sm text-ink-soft sm:table-cell">{top ? `${top} takes the most time.` : "Enter hours to see the breakdown."}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <h4 className="mt-8 font-display text-xl font-medium">Reflection</h4>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <AutoTextarea id="wa-most" label="What generates the greatest workload?" rows={3} value={data.most} onChange={(v) => setData({ ...data, most: v })} testid="wa-most" />
        <AutoTextarea id="wa-streamline" label="What could be streamlined?" rows={3} value={data.streamline} onChange={(v) => setData({ ...data, streamline: v })} testid="wa-streamline" />
        <AutoTextarea id="wa-support" label="What support would help most?" rows={3} value={data.support} onChange={(v) => setData({ ...data, support: v })} testid="wa-support" />
      </div>
    </ToolShell>
  );
};
